import { Injectable } from '@angular/core';
import { SalesRegister, Company } from './api.service';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class InvoicePrintService {
    private simpleProducts = ['FO', 'SKO', 'LDO', 'SOLVENT', 'KERO', 'POL'];

    private fmtDate(d?: string): string {
        if (!d) return '';
        const dt = new Date(d);
        return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    private num(x: any): string {
        if (x === null || x === undefined || x === '') return '-';
        const n = Number(x);
        return isNaN(n) ? '-' : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    private v(x: any): string {
        return x === null || x === undefined ? '' : String(x);
    }
    private img(path?: string): string {
        return path ? environment.serverUrl + path : '';
    }
    private isSimple(product?: string): boolean {
        return this.simpleProducts.includes((product || '').trim().toUpperCase());
    }

    private header(c: Company): string {
        const logo = this.img(c.logoPath);
        return `
            <div class="head">
                ${logo ? `<img src="${logo}" class="logo" />` : '<div class="logo-space"></div>'}
                <div class="head-center">
                    <div class="cname">${this.v(c.name)}</div>
                    <div class="caddr">${this.v(c.address)}</div>
                </div>
                <div class="logo-space"></div>
            </div>
            <div class="title-plain">SALES TAX INVOICE</div>`;
    }

    // ---------- SIMPLE (FO, Kero, etc.) ----------
    private simpleHtml(r: SalesRegister, c: Company): string {
        const sig = this.img(c.signaturePath);
        const stamp = this.img(c.stampPath);

        const sigBlock = c.hasSignature
            ? `<div class="sig-row">${stamp ? `<img src="${stamp}" class="stamp" />` : ''}${sig ? `<img src="${sig}" class="sig" />` : ''}</div>`
            : `<p class="note">NOTE : This is system generated document which does not require any signature or stamp</p>`;

        return `
        <div class="inv">
            ${this.header(c)}
            <div class="inv-no-row"><span><b>INVOICE NO: ${this.v(r.documentNumber)}</b></span><span><b>${this.fmtDate(r.documentDate)}</b></span></div>

            <div class="two-col">
                <table class="t b noinner">
                    <tr><td class="lbl">Supplier Name:</td><td>${this.v(c.name)}</td></tr>
                    <tr><td class="lbl">Address:</td><td>${this.v(c.address)}</td></tr>
                    <tr><td class="lbl">NTN No:</td><td>${this.v(c.ntn)}</td></tr>
                    <tr><td class="lbl">STRN No:</td><td>${this.v(c.strn)}</td></tr>
                </table>
                <table class="t b noinner">
                    <tr><td class="lbl">Customer Name:</td><td>${this.v(r.customerName)}</td></tr>
                    <tr><td class="lbl">Address:</td><td>${this.v(r.address)}</td></tr>
                    <tr><td class="lbl">NTN/CNIC No:</td><td>${this.v(r.ntn)}</td></tr>
                    <tr><td class="lbl">STRN No:</td><td></td></tr>
                </table>
            </div>

            <table class="t prod">
                <tr class="hd">
                    <th>Product Code</th><th>Units</th><th>Unit Price</th><th>Quantity</th>
                    <th>Total Value Excl. S.Tax</th><th>Sales Tax</th><th>Total Invoice Amount</th>
                </tr>
                <tr class="ctr">
                    <td>${this.v(r.product)}</td><td>Ltr</td><td>${this.num(r.unitPrice)}</td><td>${this.num(r.quantityLiters)}</td>
                    <td>${this.num(r.valueOfSalesExclTax)}</td><td>${this.num(r.salesTax18)}</td><td>${this.num(r.totalReceivable)}</td>
                </tr>
            </table>

            ${sigBlock}
        </div>`;
    }

    // ---------- DETAILED (PMG, HSD) ----------
    private detailedHtml(r: SalesRegister, c: Company): string {
        const sig = this.img(c.signaturePath);
        const stamp = this.img(c.stampPath);
        const tl = this.v(r.tlNumber);
        const desc = (r.product || '').trim().toUpperCase() === 'PMG' ? 'Motor Spirit' : 'Diesel';
        const amount = (Number(r.quantityLiters) || 0) * (Number(r.unitPrice) || 0);

        let chambers = '';
        for (let i = 1; i <= 6; i++) {
            chambers += `<tr><td class="ch">Chamber -0${i}</td><td></td><td></td><td></td><td></td></tr>`;
        }

        const sigFooter = c.hasSignature
            ? `<tr><td colspan="4" class="sig-cell">${stamp ? `<img src="${stamp}" class="stamp-sm" />` : ''}${sig ? `<img src="${sig}" class="sig-sm" />` : ''}</td></tr>`
            : `<tr><td colspan="4" class="note-cell">NOTE : This is system generated document which does not require any signature or stamp</td></tr>`;

        return `
        <div class="inv">
            ${this.header(c)}

            <!-- customer + invoice meta -->
            <div class="two-col v-stretch">
                <table class="t b col8 noinner">
                    <tr><td class="lbl w30">Customer Code:</td><td>${this.v(r.customerCode)}</td></tr>
                    <tr><td class="lbl">Customer Name:</td><td>${this.v(r.customerName)}</td></tr>
                    <tr><td class="lbl">Address:</td><td>${this.v(r.address)}</td></tr>
                    <tr><td class="lbl">NTN/CNIC No:</td><td>${this.v(r.ntn)}</td></tr>
                    <tr><td class="lbl">STRN No:</td><td></td></tr>
                </table>
                <div class="col4 stack">
                    <table class="t b">
                        <tr><td class="lbl w50">Invoice No:</td><td class="ctr">${this.v(r.documentNumber)}</td></tr>
                        <tr><td class="lbl">Invoice Date:</td><td class="ctr">${this.fmtDate(r.documentDate)}</td></tr>
                        <tr><td class="lbl">Territory:</td><td class="ctr">-</td></tr>
                    </table>
                    <table class="t b">
                        <tr><td class="lbl w50">Delivery No:</td><td class="ctr">-</td></tr>
                        <tr><td class="lbl">Delivery Date/Time:</td><td class="ctr">-</td></tr>
                        <tr><td class="lbl">Shipment No:</td><td class="ctr">-</td></tr>
                        <tr><td class="lbl">Depot. Code:</td><td class="ctr">-</td></tr>
                        <tr><td class="lbl">Customer Order No:</td><td class="ctr">-</td></tr>
                    </table>
                </div>
            </div>

            <!-- ship + seal -->
            <div class="two-col v-stretch">
                <table class="t b col8">
                    <tr><th class="ctr w50">Ship From Location</th><th class="ctr w50">Ship To Location</th></tr>
                    <tr><td class="ctr tall">${this.v(r.fromLocation)}</td><td class="ctr tall">${this.v(r.address)}</td></tr>
                </table>
                <table class="t b col4">
                    <tr><th colspan="2" class="ctr">SEAL NO</th></tr>
                    <tr><td class="lbl w55">Mode of Transaction</td><td class="ctr">-</td></tr>
                    <tr><td class="lbl">Carrriage Contractor</td><td class="ctr">Self</td></tr>
                    <tr><td class="lbl">Vehicle No</td><td class="ctr">${tl}</td></tr>
                    <tr><td class="lbl">Engine No</td><td class="ctr">-</td></tr>
                    <tr><td class="lbl">Chasis No</td><td class="ctr">-</td></tr>
                    <tr><td class="lbl">Caliberation Expiry</td><td class="ctr">-</td></tr>
                    <tr><td class="lbl">Loading Slip No</td><td class="ctr">-</td></tr>
                    <tr><td class="lbl">Sales Officer Code:</td><td class="ctr">-</td></tr>
                </table>
            </div>

            <!-- product -->
            <table class="t b prod">
                <tr class="hd">
                    <th>Product Code</th><th>Product Description</th><th>Tank No</th><th>Temp/Dens</th>
                    <th>Quantity Ltrs</th><th>Net Weight</th><th>Unit Price</th><th>Amount</th>
                </tr>
                <tr class="ctr">
                    <td>${this.v(r.product)}</td><td>${desc}</td><td>${tl}</td><td>-</td>
                    <td>${this.num(r.quantityLiters)}</td><td>-</td><td>${this.num(r.unitPrice)}</td><td>${this.num(amount)}</td>
                </tr>
            </table>

            <!-- chamber + totals -->
            <div class="two-col v-stretch">
                <div class="col8 stack">
                    <table class="t b chamber">
                        <tr class="hd"><th>Tank Lori Chamber Detail</th><th>Ref. DIP</th><th>Product DIP</th><th>Receiving DIP</th><th>Diff(+-)</th></tr>
                        ${chambers}
                    </table>
                    <table class="t b urdu-box">
                        <tr><td class="urdu">.&#1705;&#1605;&#1662;&#1606;&#1740; &#1705;&#1740; &#1584;&#1605;&#1729; &#1583;&#1575;&#1585;&#1740; &#1582;&#1578;&#1605; &#1729;&#1608;&#1580;&#1575;&#1574;&#1746; &#1711;&#1740;&#1548; &#1580;&#1576; &#1587;&#1575;&#1605;&#1575;&#1606; &#1593;&#1575;&#1605; &#1705;&#1740;&#1585;&#1740;&#1574;&#1585; &#1662;&#1585; &#1662;&#1729;&#1606;&#1670;&#1746; &#1575;&#1608;&#1585; &#1608;&#1575;&#1590;&#1581; &#1585;&#1587;&#1740;&#1583; &#1575;&#1587; &#1587;&#1746; &#1605;&#1608;&#1589;&#1608;&#1604; &#1729;&#1608;&#1574;&#1740; &#1729;&#1746;</td></tr>
                    </table>
                </div>
                <table class="t b col4 totals">
                    <tr><td class="lbl w55">Total Sales Price</td><td class="ctr">-</td></tr>
                    <tr><td class="lbl">Discount %</td><td class="ctr">-</td></tr>
                    <tr><td class="lbl">TOTAL EXCL. STAX</td><td class="ctr">${this.num(r.valueOfSalesExclTax)}</td></tr>
                    <tr><td class="lbl">Sales Tax 18%</td><td class="ctr">${this.num(r.salesTax18)}</td></tr>
                    <tr><td class="lbl">TOTAL INCL. STAX</td><td class="ctr">${this.num(r.valueOfSalesExclTax)}</td></tr>
                    <tr><td class="lbl">Freight</td><td class="ctr">Self</td></tr>
                    <tr><td class="lbl">S Tax on Freight</td><td class="ctr">-</td></tr>
                    <tr><td class="lbl">Less Dealer Margin</td><td class="ctr">${this.num(r.dealerCommission)}</td></tr>
                    <tr><td class="lbl">Withholding Tax</td><td class="ctr">${this.num(r.withholdingOnDealerCommission)}</td></tr>
                    <tr><td class="lbl">Franchise Income</td><td class="ctr">${this.num(r.franchiseFee)}</td></tr>
                    <tr><td class="lbl">FED on Franchise Income</td><td class="ctr">${this.num(r.fed)}</td></tr>
                    <tr><td class="lbl">INVOICE TOTAL</td><td class="ctr">${this.num(r.totalReceivable)}</td></tr>
                </table>
            </div>

            <!-- misc + payment terms -->
            <div class="two-col v-stretch">
                <table class="t b col8 misc">
                    <tr class="hd"><th>Miscellaneous Information</th></tr>
                    <tr><td class="ctr">-</td></tr>
                    <tr><td class="ctr">-</td></tr>
                    <tr><td class="ctr">-</td></tr>
                    <tr><td class="ctr">-</td></tr>
                </table>
                <table class="t b col4">
                    <tr class="hd"><th class="lft">Payment Terms</th></tr>
                    <tr><td class="pt"><b>Note:</b> I confirm that the quality and quantity of product received against the invoice is in accordance with my requirements and is free from any adulteration.</td></tr>
                </table>
            </div>

            <!-- signature -->
            <table class="t b sign-table">
                <tr class="sign-space"><td></td><td></td><td></td><td></td></tr>
                <tr class="hd"><th class="w25">Authorized</th><th class="w25">Driver</th><th class="w25">Gate Incharge</th><th class="w25">Customer Signature &amp; Stamp</th></tr>
                ${sigFooter}
            </table>
        </div>`;
    }

    private styles(): string {
        return `
        <style>
            * { font-family: Arial, Helvetica, sans-serif; box-sizing: border-box; }
            body { margin: 0; color: #000; font-size: 10px; }
            .inv { width: 100%; padding: 5mm; page-break-after: always; }
            .inv:last-child { page-break-after: auto; }

            .head { display: flex; align-items: center; margin-bottom: 6px; }
            .logo { max-height: 50px; max-width: 80px; object-fit: contain; }
            .logo-space { width: 80px; }
            .head-center { flex: 1; text-align: center; }
            .cname { font-size: 19px; font-weight: bold; }
            .caddr { font-size: 10px; }
            .title-plain { text-align: center; font-size: 15px; font-weight: bold; margin: 6px 0 10px; }

            .inv-no-row { display: flex; justify-content: space-between; margin: 10px 0; font-size: 12px; }

            .two-col { display: flex; gap: 8px; margin-bottom: 8px; }
            .two-col.v-stretch { align-items: stretch; }
            .col8 { flex: 1.9; }
            .col4 { flex: 1; }
            .stack { display: flex; flex-direction: column; gap: 6px; }
            .stack .t { height: auto; }
            .two-col.v-stretch .col8.stack { display: flex; flex-direction: column; }
            .col8.stack .urdu-box { flex: 1; display: table; }
            .two-col.v-stretch .col8.stack { display: flex; flex-direction: column; }
            .col8.stack .urdu-box { flex: 1; display: table; }

            table.t { border-collapse: collapse; width: 100%; }
            table.t.b { border: 1px solid #000; }
            .t td, .t th { border: 1px solid #000; padding: 3px 6px; font-size: 10px; text-align: left; vertical-align: middle; }
            .t .lbl { font-weight: bold; }
            .t .ctr { text-align: center; }
            .t .lft { text-align: left; }
            .t .hd th { background: #ffffff; font-weight: bold; text-align: center; }
            .w25 { width: 25%; } .w30 { width: 30%; } .w50 { width: 50%; } .w55 { width: 55%; }

            .tall { height: 80px; vertical-align: middle; }

            .prod { margin: 8px 0; }
            .prod .hd th { font-weight: bold; text-align: center; }
            .prod td { text-align: center; }

            .chamber td, .chamber th { height: 17px; font-size: 9px; padding: 2px 5px; }
            .chamber .ch { font-weight: bold; }
            .totals td { height: 17px; }

            .misc td, .misc th { height: 18px; }
            .noinner td { border: none; }
            .urdu-box { margin-top: 6px; flex: 1; }
            .urdu-box .urdu { direction: rtl; text-align: right; font-size: 11px; padding: 8px; vertical-align: top; }
            .pt { font-size: 9px; line-height: 1.4; }

            .sign-table { margin-top: 8px; }
            .sign-table .sign-space td { height: 75px; }
            .sign-table .hd th { font-weight: bold; }
            .note-cell { color: #d00; font-weight: bold; text-align: left; padding: 5px; }
            .sig-cell { text-align: right !important; padding: 6px 10px; white-space: nowrap; }
            .sig-cell .stamp-sm { max-height: 70px; vertical-align: middle; }
            .sig-cell .sig-sm { max-height: 55px; vertical-align: middle; margin-left: 8px; }

            /* simple */
            .sig-row { display: flex; justify-content: flex-end; gap: 10px; margin-top: 40px; }
            .stamp { max-height: 90px; } .sig { max-height: 60px; }
            .note { color: #d00; margin-top: 30px; font-weight: 500; }

            @media print { @page { size: A4 portrait; margin: 8mm; } .inv { padding: 0; } }
        </style>`;
    }

    print(records: SalesRegister[], companies: Company[]): void {
        const companyMap = new Map<number, Company>();
        companies.forEach((c) => companyMap.set(c.id, c));

        const slips = records
            .map((r) => {
                const c = companyMap.get(r.companyId) || ({} as Company);
                return this.isSimple(r.product) ? this.simpleHtml(r, c) : this.detailedHtml(r, c);
            })
            .join('');

        const html = `<html><head><title>Sales Tax Invoice</title>${this.styles()}</head>
            <body>${slips}<script>window.onload=function(){ setTimeout(function(){ window.print(); }, 400); }</script></body></html>`;

        const win = window.open('', '_blank', 'width=900,height=1000');
        if (win) {
            win.document.open();
            win.document.write(html);
            win.document.close();
        }
    }
}
